import React, { useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Chip,
  Paper,
  Divider,
  TextField,
  Autocomplete,
} from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import {
  COLOR_PALETTE_VALUES,
  CULTURAL_CONTEXT_VALUES,
  MOOD_VALUES,
  SEASONALITY_VALUES,
  type ColorPalette,
  type CulturalContext,
  type Mood,
  type Seasonality,
} from '../../contracts/openapi/assetContracts';
import {
  colorPaletteLabels,
  culturalContextLabels,
  moodLabels,
  seasonalityLabels,
} from '../../contracts/openapi/assetEnumLabels';
import { environmentSafeLog } from '../../utils/environment';
import { WorldsLayerMetadata } from '../../types/referencelayerMetadata';
import { API_STRING_MAX_LENGTH, WORLDS_METADATA } from '../../constants/apiMetadataConstraints';
import {
  buildEnumOptions,
  coerceEnumArray,
  coerceEnumValue,
} from './enumFieldUtils';

interface WorldsMetadataEditorProps {
  layerMetadata: WorldsLayerMetadata;
  isEditable?: boolean;
  onMetadataChange?: (updatedMetadata: WorldsLayerMetadata) => void;
  // Legacy support during migration
  asset?: any;
  // AI-generated description for fallback pattern (Issue #15)
  aiGeneratedDescription?: string;
}

/**
 * Apply intelligent defaults for Worlds layer to prevent null/undefined/empty values
 * This ensures all fields have sensible defaults to avoid backend validation errors
 * Similar to Stars layer implementation - provides smart fallbacks
 * when AI doesn't detect values or returns null/empty fields
 */
export const applyIntelligentWorldsDefaults = (data: any) => {
  // Start with existing data, then apply defaults for any null/undefined values
  const defaults = {
    // Core world information - mandatory fields must have non-empty defaults
    worldName: 'Unknown World',
    locationType: 'Unknown', // Mandatory field - must have default
    setting: 'Mixed',
    environmentType: 'Modern',
    theme: '', // Let AI detect

    // Atmosphere & Mood
    atmosphere: 'neutral',
    mood: 'neutral',
    moodAtmosphere: {},

    // Lighting & Time
    lighting: 'Natural',
    lightingStyle: 'Natural',
    timeOfDay: 'Day',

    // Spatial Characteristics
    spatialSize: 'Medium',
    settingCategory: '', // Let AI detect
    specific_location: '', // Let AI detect
    spaceSize: 'Medium',
    layout: '', // Let AI detect
    depth: '', // Let AI detect

    // Environmental Conditions
    seasonality: 'All-Season',
    weather: 'Clear',
    weatherConditions: 'Clear',

    // Cultural & Architectural
    // REMOVED: culturalContext - use cultural_context (string) instead
    culturalOrigin: '',
    architecturalStyle: '', // Let AI detect

    // Visual Palette
    colorPalette: 'Neutral',
    color_palette: 'Neutral',
    dominantColors: '',
    dominant_colors: [],

    // Style & Energy
    style: '', // Let AI detect
    energy: 'medium',

    // Tags & Description
    tags: '',
    description: '',

    // V2.0 compatibility
    layerType: 'worlds',
  };

  // Apply defaults only for null, undefined values (NOT empty strings)
  const result = { ...data };

  // Apply defaults for null/undefined fields
  Object.keys(defaults).forEach(key => {
    if (result[key] === null || result[key] === undefined) {
      const defaultValue = defaults[key as keyof typeof defaults];
      if (Array.isArray(defaultValue)) {
        result[key] = [];
      } else if (typeof defaultValue === 'object' && defaultValue !== null && !Array.isArray(defaultValue)) {
        result[key] = {};
      } else {
        result[key] = defaultValue;
      }
    }
  });

  // Ensure arrays are arrays (not null/undefined)
  if (!Array.isArray(result.dominant_colors)) {
    result.dominant_colors = [];
  }

  // Ensure objects are objects
  if (!result.moodAtmosphere || typeof result.moodAtmosphere !== 'object') {
    result.moodAtmosphere = {};
  }
  // Remove culturalContext object initialization - we use cultural_context (string) instead
  // Delete culturalContext if it exists as an object to prevent it from overriding cultural_context
  if (result.culturalContext && typeof result.culturalContext === 'object' && !Array.isArray(result.culturalContext)) {
    delete result.culturalContext;
  }

  // Remove genre; it is not part of Worlds API.
  if ('genre' in result) {
    delete result.genre;
  }

  return result;
};

const WorldsMetadataEditor: React.FC<WorldsMetadataEditorProps> = ({
  layerMetadata,
  asset,
  isEditable = false,
  onMetadataChange,
  aiGeneratedDescription,
}) => {
  // V2.0 ARCHITECTURE: Use layerMetadata first, with legacy fallback
  const worldMetadata = React.useMemo(() => {
    // Priority 1: V2.0 layerMetadata prop (preferred)
    if (layerMetadata) {
      environmentSafeLog('📌 [WORLDS V2.0] Using layerMetadata prop');
      // Apply intelligent defaults even when layerMetadata is provided
      const processedMetadata = applyIntelligentWorldsDefaults(layerMetadata);
      // Remove genre and invalid culturalContext object payloads.
      const { genre, culturalContext, ...filteredMetadata } = processedMetadata as any;
      // Also ensure culturalContext is deleted if it somehow exists
      if ('culturalContext' in filteredMetadata && typeof filteredMetadata.culturalContext === 'object' && !Array.isArray(filteredMetadata.culturalContext)) {
        delete filteredMetadata.culturalContext;
      }
      return filteredMetadata;
    }

    // Legacy fallback for backward compatibility
    console.log('🚨 [WORLDS AI CONTRACT] Full asset object received:', asset);

    environmentSafeLog(
      '🔎 [WORLDS AI CONTRACT] Extracting worldsMetadata from asset:',
      {
        hasWorldsMetadata: !!asset?.worldsMetadata,
        hasAiContractMetadata: !!asset?.aiMetadata?.layerMetadata,
        hasAiWorldsMetadata: !!asset?.aiMetadata?.worldsMetadata,
        hasMetadataWorldsMetadata: !!asset?.metadata?.worldsMetadata,
      }
    );

    let rawMetadata = null;

    // Priority 1: AI Contract layerMetadata (from AI Response Contract)
    if (asset?.aiMetadata?.layerMetadata && asset.layer === 'W') {
      environmentSafeLog(
        '📌 [WORLDS AI CONTRACT] Using aiMetadata.layerMetadata from AI Response Contract'
      );
      rawMetadata = asset.aiMetadata.layerMetadata;
    }
    // Priority 2: Root level worldsMetadata (legacy AI-generated)
    else if (asset.worldsMetadata) {
      environmentSafeLog(
        '📌 [WORLDS AI CONTRACT] Using root worldsMetadata (legacy)'
      );
      rawMetadata = asset.worldsMetadata;
    }
    // Priority 3: AI metadata structure (nested legacy)
    else if (asset.aiMetadata?.worldsMetadata) {
      environmentSafeLog(
        '📌 [WORLDS AI CONTRACT] Using aiMetadata.worldsMetadata (legacy)'
      );
      rawMetadata = asset.aiMetadata.worldsMetadata;
    }
    // Priority 4: Legacy metadata structure
    else if (asset.metadata?.worldsMetadata) {
      environmentSafeLog(
        '📌 [WORLDS AI CONTRACT] Using metadata.worldsMetadata (legacy)'
      );
      rawMetadata = asset.metadata.worldsMetadata;
    } else {
      environmentSafeLog(
        '⚠️ [WORLDS AI CONTRACT] No world metadata found, using empty object'
      );
      rawMetadata = {};
    }

    // Apply nested data access pattern for backward compatibility
    const actualWorldMetadata =
      rawMetadata?.worldsMetadata ||
      rawMetadata?.data?.worldsMetadata ||
      rawMetadata;
    environmentSafeLog(
      '🔄 [WORLDS AI CONTRACT] Final metadata after nested access:',
      actualWorldMetadata
    );

    // INTELLIGENT DEFAULTS: Apply smart defaults to handle null/missing values
    const processedMetadata = applyIntelligentWorldsDefaults(actualWorldMetadata);

    // Remove genre and invalid culturalContext object payloads.
    const { genre, culturalContext, ...filteredMetadata } = processedMetadata as any;
    // Also ensure culturalContext is deleted if it somehow exists
    if ('culturalContext' in filteredMetadata && typeof filteredMetadata.culturalContext === 'object' && !Array.isArray(filteredMetadata.culturalContext)) {
      delete filteredMetadata.culturalContext;
    }

    environmentSafeLog(
      '🌍 [WORLDS INTELLIGENT DEFAULTS] Applied smart defaults to Worlds metadata',
      { original: actualWorldMetadata, processed: filteredMetadata }
    );

    return filteredMetadata;
  }, [layerMetadata, asset]);

  // State for editable fields - initialize with extracted metadata
  const [editableData, setEditableData] = React.useState(() => {
    console.log(
      '🚀 [WORLDS INFO INIT] Initializing editableData state with worldMetadata:',
      worldMetadata
    );

    // CRITICAL FIX (Issue #15): Auto-populate description with AI-generated description
    // Pattern: AI description → creator description → empty string
    const initialDescription =
      aiGeneratedDescription ||
      worldMetadata?.creatorDescription ||
      worldMetadata?.description ||
      '';
    if (aiGeneratedDescription && !worldMetadata?.creatorDescription && !worldMetadata?.description) {
      console.log('📝 [WORLDS INFO] Auto-populating World Description with AI-generated description:', aiGeneratedDescription);
    }

    return {
      // Creator Input (CRITICAL for AI)
      creatorDescription: initialDescription,

      worldName: worldMetadata?.worldName || '',
      locationType: worldMetadata?.locationType || '',
      settingCategory: worldMetadata?.settingCategory || '',
      specific_location: worldMetadata?.specific_location || '',
      spaceSize: worldMetadata?.spaceSize || '',
      layout: worldMetadata?.layout || '',
      depth: worldMetadata?.depth || '',
      lighting: worldMetadata?.lighting || '',
      lighting_direction: worldMetadata?.lighting_direction || '',
      color_palette: coerceEnumValue(
        worldMetadata?.colorPalette ?? worldMetadata?.color_palette,
        COLOR_PALETTE_VALUES
      ),
      dominant_colors: Array.isArray(worldMetadata?.dominant_colors)
        ? worldMetadata?.dominant_colors
        : typeof worldMetadata?.dominant_colors === 'string' &&
          worldMetadata?.dominant_colors
        ? worldMetadata.dominant_colors.split(',').map((c: string) => c.trim())
        : [],
      mood: coerceEnumArray(worldMetadata?.mood, MOOD_VALUES, 3),
      atmosphere: worldMetadata?.atmosphere || '',
      vibe: worldMetadata?.vibe || '',
      weather: worldMetadata?.weather || '',
      time_of_day: worldMetadata?.time_of_day || '',
      season: coerceEnumValue(
        worldMetadata?.season ?? worldMetadata?.seasonality,
        SEASONALITY_VALUES
      ),
      geographic_region: worldMetadata?.geographic_region || '',
      culturalContext: coerceEnumArray(
        worldMetadata?.culturalContext ?? worldMetadata?.cultural_context,
        CULTURAL_CONTEXT_VALUES,
        10
      ),
      architectural_style: worldMetadata?.architectural_style || '',
      backdrop_type: worldMetadata?.backdrop_type || '',
      production_value: worldMetadata?.production_value || '',
      technical_complexity: worldMetadata?.technical_complexity || '',
      movement_space: worldMetadata?.movement_space || '',
      audio_acoustics: worldMetadata?.audio_acoustics || '',
      crowd_capacity: worldMetadata?.crowd_capacity || '',
    };
  });

  // Track metadata hash to prevent infinite update loops
  const metadataHashRef = useRef<string>('');
  const computedHash = JSON.stringify(worldMetadata);

  // 🔧 FIX: Use ref to track latest editableData for cleanup without causing re-renders
  const editableDataRef = useRef(editableData);
  useEffect(() => {
    editableDataRef.current = editableData;
  }, [editableData]);

  // Update editableData when worldMetadata changes (from AI generation)
  React.useEffect(() => {
    if (computedHash !== metadataHashRef.current) {
      console.log(
        '🔄 [WORLDS INFO] worldMetadata changed, updating editableData'
      );
      metadataHashRef.current = computedHash;

      setEditableData({
        // Creator Input (CRITICAL for AI)
        creatorDescription: worldMetadata?.creatorDescription || '',

        worldName: worldMetadata?.worldName || '',
        locationType: worldMetadata?.locationType || '',
        settingCategory: worldMetadata?.settingCategory || '',
        specific_location: worldMetadata?.specific_location || '',
        spaceSize: worldMetadata?.spaceSize || '',
        layout: worldMetadata?.layout || '',
        depth: worldMetadata?.depth || '',
        lighting: worldMetadata?.lighting || '',
        lighting_direction: worldMetadata?.lighting_direction || '',
        color_palette: coerceEnumValue(
          worldMetadata?.colorPalette ?? worldMetadata?.color_palette,
          COLOR_PALETTE_VALUES
        ),
        dominant_colors: Array.isArray(worldMetadata?.dominant_colors)
          ? worldMetadata?.dominant_colors
          : typeof worldMetadata?.dominant_colors === 'string' &&
            worldMetadata?.dominant_colors
          ? worldMetadata.dominant_colors
              .split(',')
              .map((c: string) => c.trim())
          : [],
        mood: coerceEnumArray(worldMetadata?.mood, MOOD_VALUES, 3),
        atmosphere: worldMetadata?.atmosphere || '',
        vibe: worldMetadata?.vibe || '',
        weather: worldMetadata?.weather || '',
        time_of_day: worldMetadata?.time_of_day || '',
        season: coerceEnumValue(
          worldMetadata?.season ?? worldMetadata?.seasonality,
          SEASONALITY_VALUES
        ),
        geographic_region: worldMetadata?.geographic_region || '',
        culturalContext: coerceEnumArray(
          worldMetadata?.culturalContext ?? worldMetadata?.cultural_context,
          CULTURAL_CONTEXT_VALUES,
          10
        ),
        architectural_style: worldMetadata?.architectural_style || '',
        backdrop_type: worldMetadata?.backdrop_type || '',
        production_value: worldMetadata?.production_value || '',
        technical_complexity: worldMetadata?.technical_complexity || '',
        movement_space: worldMetadata?.movement_space || '',
        audio_acoustics: worldMetadata?.audio_acoustics || '',
        crowd_capacity: worldMetadata?.crowd_capacity || '',
      });
    }
  }, [computedHash, worldMetadata]);

  // Required fields for step validation – short debounce so Next button enables quickly
  const WORLDS_REQUIRED_FIELDS = ['worldName', 'locationType', 'mood'];
  const REQUIRED_DEBOUNCE_MS = 100;
  const debounceTimeouts = useRef<{ [key: string]: NodeJS.Timeout }>({});
  const requiredFlushTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const buildProcessedData = useCallback(
    (current: typeof editableData): WorldsLayerMetadata => {
      const cur = current as any;
      const { genre, culturalContext, ...currentWithoutInvalidFields } = cur;
      return {
        ...currentWithoutInvalidFields,
        layerType: 'worlds',
        theme: cur.theme ?? worldMetadata?.theme ?? 'modern',
        atmosphere: current.atmosphere ?? worldMetadata?.atmosphere ?? 'neutral',
        setting: cur.setting ?? worldMetadata?.setting ?? 'indoor',
        location: cur.location ?? worldMetadata?.location ?? 'unknown',
        timePeriod: cur.timePeriod ?? worldMetadata?.timePeriod ?? 'present',
        lighting: current.lighting ?? worldMetadata?.lighting ?? 'natural',
        spatialSize: current.spaceSize ?? worldMetadata?.spatialSize ?? 'medium',
        season: coerceEnumValue(current.season, SEASONALITY_VALUES),
        weatherConditions: cur.weatherConditions ?? worldMetadata?.weatherConditions ?? 'clear',
        culturalContext: coerceEnumArray(
          current.culturalContext,
          CULTURAL_CONTEXT_VALUES,
          10
        ),
        architecturalStyle: current.architectural_style ?? worldMetadata?.architecturalStyle ?? 'modern',
        colorPalette: coerceEnumValue(
          current.color_palette,
          COLOR_PALETTE_VALUES
        ),
        mood: coerceEnumArray(current.mood, MOOD_VALUES, 3),
        moodAtmosphere: cur.moodAtmosphere ?? worldMetadata?.moodAtmosphere ?? {},
      };
    },
    [worldMetadata]
  );

  // Enhanced field change handler with debouncing (following LooksInformation pattern)
  const handleFieldChange = useCallback(
    (field: string, value: string | string[]) => {
      setEditableData(prev => ({ ...prev, [field]: value }));

      // Short debounce for required fields so validation unblocks Next quickly
      if (WORLDS_REQUIRED_FIELDS.includes(field) && onMetadataChange) {
        if (requiredFlushTimeoutRef.current) {
          clearTimeout(requiredFlushTimeoutRef.current);
        }
        requiredFlushTimeoutRef.current = setTimeout(() => {
          requiredFlushTimeoutRef.current = null;
          setEditableData(current => {
            onMetadataChange(buildProcessedData(current));
            return current;
          });
        }, REQUIRED_DEBOUNCE_MS);
      }

      if (debounceTimeouts.current[field]) {
        clearTimeout(debounceTimeouts.current[field]);
      }

      debounceTimeouts.current[field] = setTimeout(() => {
        if (onMetadataChange) {
          setEditableData(current => {
            onMetadataChange(buildProcessedData(current));
            return current;
          });
        }
      }, 500);
    },
    [onMetadataChange, buildProcessedData]
  );

  // Cleanup timeouts on unmount AND flush pending changes
  // 🔧 FIX: Use empty dependency array to only run on unmount, use ref for latest data
  React.useEffect(() => {
    return () => {
      if (requiredFlushTimeoutRef.current) {
        clearTimeout(requiredFlushTimeoutRef.current);
        requiredFlushTimeoutRef.current = null;
      }
      // CRITICAL FIX: Flush all pending debounced changes before unmount
      Object.values(debounceTimeouts.current).forEach(timeout => {
        clearTimeout(timeout);
      });

      if (onMetadataChange) {
        const processedData = buildProcessedData(editableDataRef.current as any);
        onMetadataChange(processedData);
        console.log('💾 [WORLDS METADATA] Flushing pending changes on unmount:', processedData);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Memoized dropdown options to prevent unnecessary re-renders
  const locationTypeOptions = useMemo(
    () => ['Indoor', 'Outdoor', 'Mixed', 'Virtual'],
    []
  );

  const settingCategoryOptions = useMemo(
    () => [
      'Studio',
      'Theater',
      'Concert Hall',
      'Street',
      'Park',
      'Beach',
      'Club',
      'Home',
      'Office',
      'School',
      'Gallery',
      'Museum',
      'Restaurant',
      'Rooftop',
      'Warehouse',
    ],
    []
  );

  const spaceSizeOptions = useMemo(
    () => ['Small', 'Medium', 'Large', 'Very Large', 'Massive'],
    []
  );

  const layoutOptions = useMemo(
    () => [
      'Open',
      'Sectioned',
      'Linear',
      'Circular',
      'Multi-level',
      'Intimate',
      'Grand',
    ],
    []
  );

  const depthOptions = useMemo(
    () => ['Shallow', 'Medium', 'Deep', 'Very Deep'],
    []
  );

  const lightingOptions = useMemo(
    () => [
      'Studio',
      'Natural',
      'Stage',
      'Ambient',
      'Dramatic',
      'Soft',
      'Harsh',
      'Colored',
    ],
    []
  );

  const lightingDirectionOptions = useMemo(
    () => ['Front', 'Back', 'Side', 'Top', 'Multiple', 'Ambient'],
    []
  );

  const colorPaletteOptions = useMemo(
    () => buildEnumOptions(COLOR_PALETTE_VALUES, colorPaletteLabels),
    []
  );

  const dominantColorsOptions = useMemo(
    () => [
      'White',
      'Black',
      'Gray',
      'Red',
      'Blue',
      'Green',
      'Yellow',
      'Purple',
      'Orange',
      'Pink',
      'Brown',
      'Gold',
      'Silver',
      'Beige',
    ],
    []
  );

  const moodOptions = useMemo(
    () => buildEnumOptions(MOOD_VALUES, moodLabels),
    []
  );

  const atmosphereOptions = useMemo(
    () => [
      'Focused',
      'Relaxed',
      'Energetic',
      'Calm',
      'Intense',
      'Inspiring',
      'Creative',
      'Professional',
      'Social',
      'Private',
    ],
    []
  );

  const vibeOptions = useMemo(
    () => [
      'Creative',
      'Professional',
      'Urban',
      'Natural',
      'Modern',
      'Classic',
      'Edgy',
      'Sophisticated',
      'Casual',
      'Formal',
    ],
    []
  );

  const weatherOptions = useMemo(
    () => ['Indoor', 'Sunny', 'Cloudy', 'Rainy', 'Stormy', 'Clear', 'Overcast'],
    []
  );

  const timeOfDayOptions = useMemo(
    () => [
      'Dawn',
      'Morning',
      'Day',
      'Afternoon',
      'Evening',
      'Night',
      'Late Night',
    ],
    []
  );

  const seasonOptions = useMemo(
    () => buildEnumOptions(SEASONALITY_VALUES, seasonalityLabels),
    []
  );

  const culturalContextOptions = useMemo(
    () => buildEnumOptions(CULTURAL_CONTEXT_VALUES, culturalContextLabels),
    []
  );

  const productionValueOptions = useMemo(
    () => ['Low', 'Medium', 'High', 'Professional', 'Cinematic'],
    []
  );

  const technicalComplexityOptions = useMemo(
    () => ['Simple', 'Moderate', 'Complex', 'Very Complex'],
    []
  );

  const acousticsOptions = useMemo(
    () => ['Poor', 'Fair', 'Good', 'Excellent', 'Perfect'],
    []
  );

  const crowdCapacityOptions = useMemo(
    () => ['Small', 'Medium', 'Large', 'Very Large', 'Massive'],
    []
  );

  // Don't render if no metadata available
  if (
    !worldMetadata &&
    !asset.worldsMetadata &&
    !asset.aiMetadata?.worldsMetadata
  ) {
    return null;
  }

  return (
    <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <PublicIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          World Metadata
        </Typography>
      </Box>

      <Grid container spacing={2}>
        {/* Location & Setting */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}
          >
            Location & Setting
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <TextField
              required
              fullWidth
              label="World Name"
              value={editableData.worldName || ''}
              onChange={(e) => handleFieldChange('worldName', e.target.value)}
              size="small"
              inputProps={{ maxLength: API_STRING_MAX_LENGTH }}
              error={!editableData.worldName || editableData.worldName.trim() === ''}
              helperText={(!editableData.worldName || editableData.worldName.trim() === '' ? 'World name is required. ' : '') + `${API_STRING_MAX_LENGTH} characters max`}
            />
          ) : (
            <Typography variant="body2">
              <strong>World Name:</strong>{' '}
              {editableData.worldName || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={locationTypeOptions}
              value={editableData.locationType}
              inputValue={editableData.locationType}
              onInputChange={(event, newValue) =>
                handleFieldChange('locationType', newValue || '')
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Location Type"
                  required
                  size="small"
                  inputProps={{ ...params.inputProps, maxLength: API_STRING_MAX_LENGTH }}
                  error={!editableData.locationType || editableData.locationType.trim() === ''}
                  helperText={(!editableData.locationType || editableData.locationType.trim() === '' ? 'Location type is required. ' : '') + `${API_STRING_MAX_LENGTH} characters max`}
                />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Location Type:</strong>{' '}
              {editableData.locationType || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={settingCategoryOptions}
              value={editableData.settingCategory}
              inputValue={editableData.settingCategory}
              onInputChange={(event, newValue) =>
                handleFieldChange('settingCategory', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Setting Category" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Setting Category:</strong>{' '}
              {editableData.settingCategory || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <TextField
              fullWidth
              label="Specific Location"
              value={editableData.specific_location}
              onChange={e =>
                handleFieldChange('specific_location', e.target.value)
              }
              size="small"
              inputProps={{ maxLength: API_STRING_MAX_LENGTH }}
            />
          ) : (
            <Typography variant="body2">
              <strong>Specific Location:</strong>{' '}
              {editableData.specific_location || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={spaceSizeOptions}
              value={editableData.spaceSize}
              inputValue={editableData.spaceSize}
              onInputChange={(event, newValue) =>
                handleFieldChange('spaceSize', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Space Size" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Space Size:</strong>{' '}
              {editableData.spaceSize || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Divider sx={{ width: '100%', my: 2 }} />

        {/* Physical Characteristics */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}
          >
            Physical Characteristics
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={layoutOptions}
              value={editableData.layout}
              inputValue={editableData.layout}
              onInputChange={(event, newValue) =>
                handleFieldChange('layout', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Layout" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Layout:</strong> {editableData.layout || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={depthOptions}
              value={editableData.depth}
              inputValue={editableData.depth}
              onInputChange={(event, newValue) =>
                handleFieldChange('depth', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Depth" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Depth:</strong> {editableData.depth || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={spaceSizeOptions}
              value={editableData.movement_space}
              inputValue={editableData.movement_space}
              onInputChange={(event, newValue) =>
                handleFieldChange('movement_space', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Movement Space" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Movement Space:</strong>{' '}
              {editableData.movement_space || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Divider sx={{ width: '100%', my: 2 }} />

        {/* Lighting & Visual */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}
          >
            Lighting & Visual
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={lightingOptions}
              value={editableData.lighting}
              inputValue={editableData.lighting}
              onInputChange={(event, newValue) =>
                handleFieldChange('lighting', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Lighting Type" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Lighting:</strong>{' '}
              {editableData.lighting || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={lightingDirectionOptions}
              value={editableData.lighting_direction}
              inputValue={editableData.lighting_direction}
              onInputChange={(event, newValue) =>
                handleFieldChange('lighting_direction', newValue || '')
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Lighting Direction"
                  size="small"
                />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Lighting Direction:</strong>{' '}
              {editableData.lighting_direction || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              options={colorPaletteOptions}
              value={colorPaletteOptions.find(option => option.value === editableData.color_palette) ?? null}
              onChange={(event, newValue) =>
                handleFieldChange('color_palette', newValue?.value || '')
              }
              renderInput={params => (
                <TextField {...params} label="Color Palette" size="small" />
              )}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              getOptionLabel={option => option.label}
            />
          ) : (
            <Typography variant="body2">
              <strong>Color Palette:</strong>{' '}
              {editableData.color_palette ? colorPaletteLabels[editableData.color_palette as ColorPalette] : 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <Autocomplete
              multiple
              fullWidth
              freeSolo
              options={dominantColorsOptions}
              value={(editableData.dominant_colors || []).slice(0, WORLDS_METADATA.dominantColors.maxItems)}
              onChange={(event, newValue) =>
                handleFieldChange('dominant_colors', (newValue ?? []).slice(0, WORLDS_METADATA.dominantColors.maxItems))
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Dominant Colors"
                  size="small"
                  inputProps={{ ...params.inputProps, maxLength: API_STRING_MAX_LENGTH }}
                  helperText={`Max ${WORLDS_METADATA.dominantColors.maxItems} items, ${API_STRING_MAX_LENGTH} chars each`}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    {...getTagProps({ index })}
                  />
                ))
              }
            />
          ) : (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                Dominant Colors:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(editableData.dominant_colors || []).map((item: string, index: number) => (
                  <Chip key={index} label={item} size="small" variant="outlined" />
                ))}
                {(!editableData.dominant_colors?.length) && (
                  <Typography variant="body2" color="text.secondary">Not specified</Typography>
                )}
              </Box>
            </Box>
          )}
        </Grid>

        <Grid item xs={12} md={12}>
          {isEditable ? (
            <TextField
              fullWidth
              label="Backdrop Type"
              value={editableData.backdrop_type}
              onChange={e => handleFieldChange('backdrop_type', e.target.value)}
              size="small"
              inputProps={{ maxLength: API_STRING_MAX_LENGTH }}
            />
          ) : (
            <Typography variant="body2">
              <strong>Backdrop Type:</strong>{' '}
              {editableData.backdrop_type || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Divider sx={{ width: '100%', my: 2 }} />

        {/* Mood & Atmosphere */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}
          >
            Mood & Atmosphere
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              multiple
              options={moodOptions}
              value={moodOptions.filter(option =>
                (editableData.mood || []).includes(option.value)
              )}
              onChange={(event, newValue) =>
                handleFieldChange('mood', newValue.map(option => option.value).slice(0, 3))
              }
              renderInput={params => (
                <TextField {...params} label="Mood" size="small" />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option.label}
                    size="small"
                    {...getTagProps({ index })}
                  />
                ))
              }
              isOptionEqualToValue={(option, value) => option.value === value.value}
              getOptionLabel={option => option.label}
            />
          ) : (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                Mood:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(editableData.mood || []).map((mood: Mood, index: number) => (
                  <Chip key={index} label={moodLabels[mood]} size="small" variant="outlined" />
                ))}
                {(!editableData.mood || editableData.mood.length === 0) && (
                  <Typography variant="body2" color="text.secondary">Not specified</Typography>
                )}
              </Box>
            </Box>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={atmosphereOptions}
              value={editableData.atmosphere}
              inputValue={editableData.atmosphere}
              onInputChange={(event, newValue) =>
                handleFieldChange('atmosphere', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Atmosphere" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Atmosphere:</strong>{' '}
              {editableData.atmosphere || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={vibeOptions}
              value={editableData.vibe}
              inputValue={editableData.vibe}
              onInputChange={(event, newValue) =>
                handleFieldChange('vibe', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Vibe" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Vibe:</strong> {editableData.vibe || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Divider sx={{ width: '100%', my: 2 }} />

        {/* Environmental Context */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}
          >
            Environmental Context
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={weatherOptions}
              value={editableData.weather}
              inputValue={editableData.weather}
              onInputChange={(event, newValue) =>
                handleFieldChange('weather', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Weather" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Weather:</strong>{' '}
              {editableData.weather || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={timeOfDayOptions}
              value={editableData.time_of_day}
              inputValue={editableData.time_of_day}
              onInputChange={(event, newValue) =>
                handleFieldChange('time_of_day', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Time of Day" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Time of Day:</strong>{' '}
              {editableData.time_of_day || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              options={seasonOptions}
              value={seasonOptions.find(option => option.value === editableData.season) ?? null}
              onChange={(event, newValue) =>
                handleFieldChange('season', newValue?.value || '')
              }
              renderInput={params => (
                <TextField {...params} label="Season" size="small" />
              )}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              getOptionLabel={option => option.label}
            />
          ) : (
            <Typography variant="body2">
              <strong>Season:</strong> {editableData.season ? seasonalityLabels[editableData.season as Seasonality] : 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <TextField
              fullWidth
              label="Geographic Region"
              value={editableData.geographic_region}
              onChange={e =>
                handleFieldChange('geographic_region', e.target.value)
              }
              size="small"
            />
          ) : (
            <Typography variant="body2">
              <strong>Geographic Region:</strong>{' '}
              {editableData.geographic_region || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <Autocomplete
              multiple
              fullWidth
              options={culturalContextOptions}
              value={culturalContextOptions.filter(option =>
                (editableData.culturalContext || []).includes(option.value)
              )}
              onChange={(event, newValue) =>
                handleFieldChange(
                  'culturalContext',
                  newValue.map(option => option.value).slice(0, 10)
                )
              }
              renderInput={params => (
                <TextField {...params} label="Cultural Context" size="small" />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option.label}
                    size="small"
                    {...getTagProps({ index })}
                  />
                ))
              }
              isOptionEqualToValue={(option, value) => option.value === value.value}
              getOptionLabel={option => option.label}
            />
          ) : (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                Cultural Context:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(editableData.culturalContext || []).map((item: CulturalContext, index: number) => (
                  <Chip key={index} label={culturalContextLabels[item]} size="small" variant="outlined" />
                ))}
                {(!editableData.culturalContext || editableData.culturalContext.length === 0) && (
                  <Typography variant="body2" color="text.secondary">Not specified</Typography>
                )}
              </Box>
            </Box>
          )}
        </Grid>

        <Grid item xs={12} md={12}>
          {isEditable ? (
            <TextField
              fullWidth
              label="Architectural Style"
              value={editableData.architectural_style}
              onChange={e =>
                handleFieldChange('architectural_style', e.target.value)
              }
              size="small"
            />
          ) : (
            <Typography variant="body2">
              <strong>Architectural Style:</strong>{' '}
              {editableData.architectural_style || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Divider sx={{ width: '100%', my: 2 }} />

        {/* Production & Technical */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}
          >
            Production & Technical
          </Typography>
        </Grid>

        <Grid item xs={12} md={3}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={productionValueOptions}
              value={editableData.production_value}
              inputValue={editableData.production_value}
              onInputChange={(event, newValue) =>
                handleFieldChange('production_value', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Production Value" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Production Value:</strong>{' '}
              {editableData.production_value || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={3}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={technicalComplexityOptions}
              value={editableData.technical_complexity}
              inputValue={editableData.technical_complexity}
              onInputChange={(event, newValue) =>
                handleFieldChange('technical_complexity', newValue || '')
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Technical Complexity"
                  size="small"
                />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Technical Complexity:</strong>{' '}
              {editableData.technical_complexity || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={3}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={acousticsOptions}
              value={editableData.audio_acoustics}
              inputValue={editableData.audio_acoustics}
              onInputChange={(event, newValue) =>
                handleFieldChange('audio_acoustics', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Audio Acoustics" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Audio Acoustics:</strong>{' '}
              {editableData.audio_acoustics || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={3}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={crowdCapacityOptions}
              value={editableData.crowd_capacity}
              inputValue={editableData.crowd_capacity}
              onInputChange={(event, newValue) =>
                handleFieldChange('crowd_capacity', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Crowd Capacity" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Crowd Capacity:</strong>{' '}
              {editableData.crowd_capacity || 'Not specified'}
            </Typography>
          )}
        </Grid>
      </Grid>
    </Paper>
  );
};

export default WorldsMetadataEditor;
